import { useEffect, useRef, useState } from "react";

/**
 * Reads volume level from an existing MediaStream using Web Audio AnalyserNode.
 * Does NOT call getUserMedia — it taps the stream the caller already has.
 *
 * Returns a smoothed level in [0, 1].
 *
 * Cleans up rAF, analyser, source, and AudioContext when:
 *  - active becomes false
 *  - stream changes
 *  - component unmounts
 */
export function useMicVolumeMeter(
  stream: MediaStream | null,
  active: boolean
): number {
  const [level, setLevel] = useState(0);
  const rafRef = useRef<number | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const smoothedRef = useRef(0);

  useEffect(() => {
    if (!active || !stream) {
      // ensure we drop to zero when inactive
      setLevel(0);
      smoothedRef.current = 0;
      return;
    }

    let cancelled = false;
    try {
      const AnyAudioCtx =
        (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
          .AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AnyAudioCtx) return;

      const ctx = new AnyAudioCtx();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);

      ctxRef.current = ctx;
      sourceRef.current = source;
      analyserRef.current = analyser;

      const buf = new Uint8Array(analyser.fftSize);

      const tick = () => {
        if (cancelled || !analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(buf);

        // RMS around 128 midline, scale to ~[0,1]
        let sumSq = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sumSq += v * v;
        }
        const rms = Math.sqrt(sumSq / buf.length);
        // Boost a touch so quiet speech still moves the meter, then clamp.
        const target = Math.min(1, rms * 2.2);
        // Lerp for nicer motion
        smoothedRef.current = smoothedRef.current * 0.7 + target * 0.3;
        setLevel(smoothedRef.current);

        rafRef.current = requestAnimationFrame(tick);
      };

      rafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      // Analyser is non-critical UX; never crash the recorder.
      // eslint-disable-next-line no-console
      console.warn("useMicVolumeMeter: failed to attach analyser", err);
    }

    return () => {
      cancelled = true;
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      try {
        sourceRef.current?.disconnect();
      } catch {
        /* noop */
      }
      try {
        analyserRef.current?.disconnect();
      } catch {
        /* noop */
      }
      const ctx = ctxRef.current;
      if (ctx && ctx.state !== "closed") {
        ctx.close().catch(() => {
          /* noop */
        });
      }
      sourceRef.current = null;
      analyserRef.current = null;
      ctxRef.current = null;
      smoothedRef.current = 0;
      setLevel(0);
    };
  }, [stream, active]);

  return level;
}
